import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '@morningstar/shared/environment';
import { SecurityGroups } from './security-groups';
import { EncryptionService } from '@morningstar/shared/encryption';
import { GenericErrorHandler } from '@morningstar/shared/base/error-handler';
import { IRouteNode } from './route-node';
import { Token } from './token';
import { UserStatus } from './user-status';
import { MenuIcons } from 'apps/wsg/src/app/shared/menu-icons';
import { IRefreshTokenService } from './refresh-token-service';

@Injectable({
  providedIn: 'root'
})
export class TokenService extends IRefreshTokenService {

  environmentName: string = environment.name;
  tokenName = `mstar-token-${this.environmentName}`;
  userStatus = new BehaviorSubject<UserStatus>(new UserStatus(this.isAuthTokenValid(), this.getSecurityGroup()));
  // Observable string streams
  userStatusChanged$ = this.userStatus.asObservable();

  constructor(private httpClient: HttpClient) {
    super();
  }

  storeToken(token: Token): void {

    Object.keys(token.routes).forEach(key => {

      const newRoute = EncryptionService.encrypt(token.routes[key]);

      token.routes[newRoute] = newRoute;
      delete token.routes[key];
    });

    localStorage.setItem(this.tokenName, JSON.stringify(token));
    this.userStatus.next(new UserStatus(true, token.securityGroup));
  }

  updateTokenSecurityGroup(securityGroup: number) {
    const token = this.getAuthToken();

    if (token == null){
      return;
    }

    token.securityGroup = securityGroup;

    this.storeToken(token);
  }

  getAuthToken(): Token {
    const jsonObject = JSON.parse(localStorage.getItem(this.tokenName));
    return <Token>jsonObject;
  }

  getTokenValue(): string {
    const jsonObject = JSON.parse(localStorage.getItem(this.tokenName));

    const token = (<Token>jsonObject);

    if (token == null) {
      return '';
    }

    return token.token;
  }

  deleteToken(){
    localStorage.removeItem(this.tokenName);
  }

  isAuthTokenValid(): boolean {
    const token = this.getAuthToken();

    if (token == null || token.expiration == null){
      return false;
    }

    return new Date(token.expiration) > new Date();
  }

  isRefreshTokenValid(): boolean {
    const token = this.getAuthToken();

    if (token == null || token.refreshToken == null || token.refreshTokenExpires == null){
      return false;
    }

    return new Date(token.refreshTokenExpires) > new Date();
  }

  getSecurityGroup(): number {
    const token = this.getAuthToken();

    if (token == null || token.expiration == null){
      return SecurityGroups.None;
    }

    return token.securityGroup;
  }

  getDefaultLocation(): string {
    const token = this.getAuthToken();

    if (token == null || token.expiration == null){
      return '';
    }

    return token.defaultLocation;
  }

  getRoutes(): Map<string, string> {
    const token = this.getAuthToken();

    if (token == null) {
      return null;
    }

    return token.routes;
  }

  getRouteNodes(): IRouteNode[] {
    const token = this.getAuthToken();

    if (token == null) {
      return null;
    }

    return token.routeNodes.filter(r => !r.hidden)
                           .map(r => {
                             return {
                               ...r,
                               icon: MenuIcons.routeIcons[r.name]
                             }
                           });
  }

  getMSClient(): string {
    const token = this.getAuthToken();

    if (token == null) {
      return null;
    }

    return token.msClient;
  }

  getRestrictedAgreementAccess(): boolean {
    const token = this.getAuthToken();

    if (token == null) {
      return false;
    }

    return token.restrictedAgreementAccess;
  }

  getUserId(): number {
    const token = this.getAuthToken();

    if (token == null) {
      return null;
    }

    return token.userId;
  }

  getGenerateGemboxInvoice(): boolean {
    const token = this.getAuthToken();

    if(token == null){
      return null;
    }

    return token.generateGemboxInvoice;
  }

  isMStarAdmin(): boolean {
    const token = this.getAuthToken();

    if (token == null) {
      return null;
    }

    return token.mstarAdmin;
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.getAuthToken();

    return this.httpClient.post<Token>(`${environment.spaceGeekUrl}${environment.api.auth.refresh}`, {
                    token: refreshToken.refreshToken,
                    userId: refreshToken.userId,
                    msClient: refreshToken.msClient,
                    application: 'WSG'
                  })
                   .pipe(
                     tap(token => this.storeToken(token)),
                     catchError(GenericErrorHandler.handleError('refreshToken'))
                   );
  }

  refreshAuthToken(): Promise<any> {

    const refreshToken = this.getAuthToken();

    return this.httpClient.post<Token>(`${environment.spaceGeekUrl}${environment.api.auth.refresh}`, {
                    token: refreshToken.refreshToken,
                    userId: refreshToken.userId,
                    msClient: refreshToken.msClient,
                    application: 'WSG'
                  })
                   .pipe(
                     tap(token => this.storeToken(token)),
                     catchError(GenericErrorHandler.handleError('refreshToken'))
                   ).toPromise();
  }

  isTRWEnabled(): boolean {
    const token = this.getAuthToken();

    if (token == null) {
      return false;
    }

    return token.isTRWEnabled;
  }

  getTRWToken(): string {
    const token = this.getAuthToken();

    if (token == null) {
      return null;
    }

    return token.trwToken;
  }

  getTRWEmail(): string {
    const token = this.getAuthToken();

    if (token == null) {
      return null;
    }

    return token.trwEmail;
  }
}